import { generatePath } from 'react-router-dom';
import {
	AccessEventAction,
	AuditAction,
	AuthEventAction,
	AuthEventMethod,
	DealEventCategory,
	HoldingEventType,
} from '@dealstack/shared/lib/backendClient';
import type {
	DealEventAccessTct,
	DealEventActorTct,
	DealEventAuthTct,
	DealEventCapitalTct,
	DealEventHoldingTct,
	DealEventLogUnreadRes,
	DealEventTct,
} from '@dealstack/shared/lib/backendClient';
import {
	extractDecimalPrecisionFromUnitDenominator,
	formatNumberToUI,
} from '@dealstack/shared/lib/format/number';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import {
	formatFieldName,
	formatTableNameToLabel,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';

type Translate = (key: string) => string;

export const formatEventActionLabel = (action: AuditAction, t: Translate): string => {
	switch (action) {
		case AuditAction.CREATE:
			return t('deal_event_log.action.create');
		case AuditAction.UPDATE:
			return t('deal_event_log.action.update');
		case AuditAction.DELETE:
			return t('deal_event_log.action.delete');
		case AuditAction.RESTORE:
			return t('deal_event_log.action.restore');
		default:
			return action;
	}
};

export const formatCategoryLabel = (category: DealEventCategory, t: Translate): string => {
	switch (category) {
		case DealEventCategory.CAPITAL:
			return t('deal_event_log.category.capital');
		case DealEventCategory.PARTICIPANT:
			return t('deal_event_log.category.participant');
		case DealEventCategory.ADMIN:
			return t('deal_event_log.category.admin');
		case DealEventCategory.MESSAGE:
			return t('deal_event_log.category.message');
		case DealEventCategory.ACCESS:
			return t('deal_event_log.category.access');
		default:
			return category;
	}
};

export const formatActorName = (actor: DealEventActorTct, t: Translate): string => {
	if (actor.isSystem) {
		return t('deal_event_log.actor.system');
	}

	return actor.displayName || actor.email || t('deal_event_log.actor.unknown');
};

export const formatChangedFields = (event: DealEventTct, t: Translate): string =>
	[
		...new Set(
			event.changedFields.map((field) => formatFieldName(field, t, event.entity.type))
		),
	].join(', ');

const formatDatedLabel = (date: string, t: Translate): string =>
	`${t('deal_event_log.capital.dated')} ${formatBEDateToUiWithFallback(date, DATE_FORMATS.CALENDAR_DATE)}`;

export const firstSeenEventIndex = (
	events: Pick<DealEventTct, 'occurredAt'>[],
	previousViewedAt: string | undefined
): number => {
	if (!previousViewedAt) {
		return -1;
	}
	const seenUntil = Date.parse(previousViewedAt);
	return events.findIndex((event) => Date.parse(event.occurredAt) <= seenUntil);
};

export const formatUnreadBadge = (
	unread: Pick<DealEventLogUnreadRes, 'count' | 'hasMore'> | undefined
): string | undefined => {
	const unreadCount = unread?.count;
	if (!unreadCount) {
		return undefined;
	}
	return unread.hasMore ? `${unreadCount}+` : String(unreadCount);
};

export const formatCapitalSummary = (capital: DealEventCapitalTct, t: Translate): string => {
	const parts: string[] = [];

	if (capital.securityName) {
		parts.push(capital.securityName);
	}

	if (capital.issuerName) {
		parts.push(capital.issuerName);
	}

	if (capital.units !== undefined) {
		const units = formatNumberToUI(
			capital.units,
			undefined,
			extractDecimalPrecisionFromUnitDenominator(capital.unitDenominator)
		);

		parts.push(`${units} ${t('deal_event_log.capital.units')}`);
	}

	if (capital.cost !== undefined) {
		parts.push(formatNumberToUI(capital.cost, capital.currency));
	}

	if (capital.date) {
		parts.push(formatDatedLabel(capital.date, t));
	}

	return parts.join(' · ');
};

export const formatCapitalParties = (capital: DealEventCapitalTct): string =>
	[capital.fromName, capital.toName].filter(Boolean).join(' → ');

export const formatHoldingEventType = (eventType: HoldingEventType, t: Translate): string => {
	switch (eventType) {
		case HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES:
			return t('holdings.event.issue');
		case HoldingEventType.PLEDGING_OF_SHARES:
			return t('holdings.event.pledge');
		case HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS:
			return t('holdings.event.assigment');
		case HoldingEventType.NOTE:
			return t('holdings.event.note');
		default:
			return eventType;
	}
};

export const formatHoldingSummary = (holding: DealEventHoldingTct, t: Translate): string =>
	[
		formatHoldingEventType(holding.eventType, t),
		holding.securityName,
		formatDatedLabel(holding.date, t),
	]
		.filter(Boolean)
		.join(' · ');

const holdingCounterparty = (holding: DealEventHoldingTct, t: Translate): string | undefined => {
	switch (holding.eventType) {
		case HoldingEventType.PLEDGING_OF_SHARES:
			return [
				holding.pledgeToName,
				holding.pledgeAgainstName &&
					`(${t('holdings.events.against_desc')} ${holding.pledgeAgainstName})`,
			]
				.filter(Boolean)
				.join(' ');
		case HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS:
			return holding.assignedToName;
		default:
			return undefined;
	}
};

export const formatHoldingParties = (holding: DealEventHoldingTct, t: Translate): string =>
	[holding.holderName, holdingCounterparty(holding, t)].filter(Boolean).join(' → ');

export const holdingDetailsPath = (dealId: string, holding: DealEventHoldingTct): string => {
	const path = generatePath(
		`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.HOLDING_DETAILS}`,
		{
			dealId,
			personId: holding.holderId,
		}
	);
	const query = new URLSearchParams({
		securityId: holding.securityId,
		holderId: holding.holderId,
	});

	return `${path}?${query.toString()}`;
};

export const formatAccessHeading = (action: AccessEventAction, t: Translate): string => {
	switch (action) {
		case AccessEventAction.GRANT:
			return t('deal_event_log.access.granted');
		case AccessEventAction.REVOKE:
			return t('deal_event_log.access.revoked');
		default:
			return t('deal_event_log.access.changed');
	}
};

export const formatRoles = (roles: string[], tRole: Translate): string =>
	roles.map((role) => tRole(`permission-role.${role}`)).join(', ');

export const formatAccessSummary = (access: DealEventAccessTct, tRole: Translate): string => {
	const before = formatRoles(access.rolesBefore, tRole);
	const after = formatRoles(access.rolesAfter, tRole);
	const roles = before && after ? `${before} → ${after}` : after || before;

	return [access.targetName || access.targetEmail, access.programmeName, roles]
		.filter(Boolean)
		.join(' · ');
};

export const formatAuthHeading = (action: AuthEventAction, t: Translate): string => {
	switch (action) {
		case AuthEventAction.SIGN_IN_SUCCEEDED:
			return t('deal_event_log.auth.sign_in_succeeded');
		case AuthEventAction.SIGN_IN_FAILED:
			return t('deal_event_log.auth.sign_in_failed');
		case AuthEventAction.MFA_SUCCEEDED:
			return t('deal_event_log.auth.mfa_succeeded');
		case AuthEventAction.EMAIL_VERIFIED:
			return t('deal_event_log.auth.email_verified');
		case AuthEventAction.RISK_DETECTED:
			return t('deal_event_log.auth.risk_detected');
		case AuthEventAction.PASSWORD_RESET_REQUESTED:
			return t('deal_event_log.auth.password_reset_requested');
		case AuthEventAction.PASSWORD_RESET_SUCCEEDED:
			return t('deal_event_log.auth.password_reset_succeeded');
		default:
			return action;
	}
};

export const formatAuthMethod = (method: AuthEventMethod, t: Translate): string => {
	switch (method) {
		case AuthEventMethod.PASSWORD:
			return t('deal_event_log.auth.method.password');
		case AuthEventMethod.OAUTH:
			return t('deal_event_log.auth.method.oauth');
		case AuthEventMethod.SSO:
			return t('deal_event_log.auth.method.sso');
		case AuthEventMethod.MAGIC_AUTH:
			return t('deal_event_log.auth.method.magic_auth');
		case AuthEventMethod.MFA:
			return t('deal_event_log.auth.method.mfa');
		case AuthEventMethod.EMAIL_VERIFICATION:
			return t('deal_event_log.auth.method.email_verification');
		default:
			return method;
	}
};

export const formatDealEventHeadingParts = (
	event: DealEventTct,
	t: Translate
): { label: string; action?: string } => {
	if (event.auth) {
		return { label: formatAuthHeading(event.auth.action, t) };
	}

	if (event.access) {
		return { label: formatAccessHeading(event.access.action, t) };
	}

	return {
		label: formatTableNameToLabel(event.entity.type, t),
		action: formatEventActionLabel(event.action, t),
	};
};

export const formatDealEventHeading = (event: DealEventTct, t: Translate): string => {
	const { label, action } = formatDealEventHeadingParts(event, t);

	return [label, action].filter(Boolean).join(' · ');
};

export const formatAuthSummary = (auth: DealEventAuthTct, t: Translate): string =>
	[auth.method && formatAuthMethod(auth.method, t), auth.errorCode].filter(Boolean).join(' · ');
